import type {FrontendTf1Season, FrontendTf1Video} from '@repo/shared/api/tf1';
import type {KohLantaEpisode, KohLantaSeason, KohLantaVideo} from '@repo/shared/api/tv_koh_lanta';

export interface Tf1SeasonGroup {
  season: FrontendTf1Season;
  videos: FrontendTf1Video[];
  downloaded: number;
  episodeParts: number;
}

export function groupTf1Seasons(
  seasons: FrontendTf1Season[],
  videos: FrontendTf1Video[],
): Tf1SeasonGroup[] {
  const groups = new Map(
    seasons.map(season => [
      season.id,
      {season, videos: [] as FrontendTf1Video[], downloaded: 0, episodeParts: 0},
    ]),
  );
  for (const video of videos) {
    let group = groups.get(video.listId);
    if (!group) {
      group = {
        season: {
          id: video.listId,
          season: video.season,
          label: video.season === undefined ? 'Extras' : `Season ${video.season}`,
          reportedVideoCount: 0,
        },
        videos: [],
        downloaded: 0,
        episodeParts: 0,
      };
      groups.set(video.listId, group);
    }
    group.videos.push(video);
    if (video.kind === 'episode') group.episodeParts++;
    if (video.kind === 'episode' && video.state === 'complete') group.downloaded++;
  }
  for (const group of groups.values())
    group.videos.sort(
      (a, b) =>
        (b.episode ?? -1) - (a.episode ?? -1) ||
        (a.part ?? 0) - (b.part ?? 0) ||
        a.title.localeCompare(b.title),
    );
  return [...groups.values()].sort(
    (a, b) =>
      (b.season.season ?? -1) - (a.season.season ?? -1) ||
      a.season.label.localeCompare(b.season.label),
  );
}

const SEASON_LABEL = /^(?:Saison|Season)\s+\d+\s*(?:[-–—:]\s*)?/iu;

export function kohLantaSeasonNames(season: {label: string; season?: number}): {
  title: string;
  subtitle: string;
} {
  const stripped = season.label.replace(SEASON_LABEL, '').trim();
  if (season.season === undefined) return {title: season.label, subtitle: ''};
  return {title: `Saison ${season.season}`, subtitle: stripped};
}

function playableVideo(video: FrontendTf1Video): KohLantaVideo {
  return {
    videoId: video.videoId,
    title: video.title,
    part: video.part,
    durationSeconds: video.durationSeconds,
    bytes: video.bytes,
  };
}

/** Seasons that have at least one completed episode, newest season and episode first. */
export function playableKohLantaSeasons(
  seasons: FrontendTf1Season[],
  videos: FrontendTf1Video[],
): KohLantaSeason[] {
  const playable: KohLantaSeason[] = [];
  for (const group of groupTf1Seasons(seasons, videos)) {
    const byEpisode = new Map<number, FrontendTf1Video[]>();
    for (const video of group.videos) {
      if (video.kind !== 'episode' || video.state !== 'complete' || video.episode === undefined)
        continue;
      const parts = byEpisode.get(video.episode) ?? [];
      parts.push(video);
      byEpisode.set(video.episode, parts);
    }
    if (!byEpisode.size) continue;
    const episodes: KohLantaEpisode[] = [...byEpisode.entries()]
      .sort((left, right) => right[0] - left[0])
      .map(([episode, parts]) => ({
        episode,
        videos: parts
          .slice()
          .sort(
            (left, right) =>
              (left.part ?? 0) - (right.part ?? 0) || left.title.localeCompare(right.title, 'fr'),
          )
          .map(playableVideo),
      }));
    playable.push({
      id: group.season.id,
      label: group.season.label,
      season: group.season.season,
      episodes,
      latestEpisode: Math.max(...episodes.map(episode => episode.episode)),
    });
  }
  return playable;
}
